import type { UIViewInjectedProps } from '@uirouter/react';
import { Form } from 'antd';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { api } from '@/api';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { DrawerFormWithNote } from '@/components/note-form';
import { PERMISSION_OBJ_LOT } from '@/constants/permissions';
import { campaignStore, lotStore } from '@/stores';
import { NoteObjectType, ProductionStatus } from '@/types/enums';
import { getTranslatedString } from '@/utils';
import { productFormattedLabel, showError } from '@/utils/common';
import { withoutRepeatValue } from '@/utils/controller';
import { useMassUpdateCashedStores } from '@/utils/store';

import { getFormFields } from './setup';

export const LotForm = observer(({ transition }: UIViewInjectedProps) => {
  const isCreate = transition.router.globals.current.name === 'base-layout.lots.create';
  const { lotId } = transition.router.globals.params;
  const lot = lotStore.items.find(({ id }: any) => id === +lotId);

  const [form] = Form.useForm();
  const [fetchingLot, setFetchingLot] = useState(false);

  const { loading } = useMassUpdateCashedStores([campaignStore]);

  const onClose = () => {
    transition.router.stateService.go('base-layout.lots');
  };

  // When editing and lot is not in store (e.g. direct link or store was refilled), fetch by id (same as LotView)
  useEffect(() => {
    if (isCreate || !lotId || lot) return;
    setFetchingLot(true);
    const request = api.lots.get(+lotId);
    request.source
      .then((response) => {
        const fullLotData = response.data;
        runInAction(() => {
          const existing = lotStore.items.find(({ id }) => id === +lotId);
          if (existing) {
            Object.assign(existing, fullLotData);
          } else {
            lotStore.addItem(fullLotData);
          }
        });
      })
      .catch(showError)
      .finally(() => setFetchingLot(false));
    return () => request.cancel();
  }, [isCreate, lotId, lot]);

  const defaultCampaign =
    isCreate && campaignStore.items.length > 0
      ? campaignStore.items.slice().sort((a, b) => Number(b.year) - Number(a.year))[0]
      : undefined;
  const defaultCampaignId = defaultCampaign?.id;

  const initValues =
    !isCreate && lot
      ? {
          ...lot,
          // unblockCvId: lot.unblockCv.id,
          executionCvId: lot.executionCv ? lot.executionCv.id : null,
          bvId: lot.bv?.id,
          campaignId: lot.campaign?.id || defaultCampaignId,
          ownerId: lot.owner?.id || null,
          productId: lot.product?.id || null,
          factoryId: lot.factory?.id ?? null,
          locationId: lot.location?.id ?? null,
          productionDate: lot.productionDate && moment(lot.productionDate),
          sacksQuantity: lot.sacksQuantity || 385,
          weight: lot.weight || 25025,
        }
      : {
          productionDate: moment(new Date()),
          sacksQuantity: 385,
          weight: 25025,
          campaignId: defaultCampaignId,
          productionStatus: ProductionStatus.Produced,
        };

  const title = isCreate
    ? getTranslatedString('lots.create-new')
    : getTranslatedString('lots.lot-number', { 0: lotId });

  const onChangeLocation = (id: number) => {
    isCreate && form.setFieldsValue({ locationId: id });
  };

  const formFields = getFormFields(onChangeLocation);
  let allowedFields = getAllowedFormFields(formFields, isCreate, PERMISSION_OBJ_LOT);

  if (!isCreate && lot) {
    allowedFields = allowedFields.map((field: any) => {
      const initialSelectedOptionsFor = (key: string) => {
        const map: Record<string, { value: number; label: string }[] | undefined> = {
          executionCvId:
            lot.executionCv != null
              ? [
                  {
                    value: lot.executionCv.id,
                    label: lot.executionCv.number ?? String(lot.executionCv.id),
                  },
                ]
              : undefined,
          bvId: lot.bv != null ? [{ value: lot.bv.id, label: lot.bv.name ?? String(lot.bv.id) }] : undefined,
          campaignId:
            lot.campaign != null
              ? [{ value: lot.campaign.id, label: lot.campaign.name ?? String(lot.campaign.id) }]
              : undefined,
          productId:
            lot.product != null ? [{ value: lot.product.id, label: productFormattedLabel(lot.product) }] : undefined,
          factoryId:
            lot.factory != null
              ? [{ value: lot.factory.id, label: lot.factory.name ?? String(lot.factory.id) }]
              : undefined,
          locationId:
            lot.location != null
              ? [{ value: lot.location.id, label: lot.location.name ?? String(lot.location.id) }]
              : undefined,
        };
        return map[key];
      };
      const opts = initialSelectedOptionsFor(field.name);
      if (!opts) return field;
      return {
        ...field,
        params: { ...field.params, initialSelectedOptions: opts },
      };
    });
  } else if (isCreate && defaultCampaign != null) {
    const campaignLabel = defaultCampaign.name?.trim() || defaultCampaign.year || String(defaultCampaign.id);
    allowedFields = allowedFields.map((field: any) => {
      if (field.name !== 'campaignId') return field;
      return {
        ...field,
        params: {
          ...field.params,
          initialSelectedOptions: [{ value: defaultCampaign.id, label: campaignLabel }],
        },
      };
    });
  }

  const resourceController = withoutRepeatValue(initValues, {
    create: (values: any) => api.lots.create(values).source.then(({ data }: any) => lotStore.addItem(data)),
    update: (values: any) => {
      const updatedValues = { ...values, isDraft: false };
      return api.lots.update(lot.id, updatedValues).source.then(({ data }: any) => lotStore.updateItem(data));
    },
  });

  return (
    <DrawerFormWithNote
      objectType={NoteObjectType.Lot}
      form={form}
      resourceId={lotId}
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={loading || (!isCreate && !lot) || fetchingLot}
    />
  );
});
